import type { SelectOption } from '../components/ui/Select'
import type { PaymentRecord, PaymentTxnStatus } from '../types'
import { dateRanges, paymentTxnStatusLabels, paymentTxnStatuses } from './payments'

/** Sentinel for the "All" status tab. */
export const ALL = 'all'

export type PaymentStatusFilter = PaymentTxnStatus | typeof ALL

export type PaymentsFilters = {
  /** Matches driver name (placeholder text in the design). */
  query: string
  status: PaymentStatusFilter
  rangeId: string
}

export const defaultPaymentFilters: PaymentsFilters = {
  query: '',
  status: ALL,
  rangeId: ALL,
}

export const paymentStatusTabs: { value: PaymentStatusFilter; label: string }[] = [
  { value: ALL, label: 'All' },
  ...paymentTxnStatuses.map((status) => ({
    value: status,
    label: paymentTxnStatusLabels[status],
  })),
]

export const paymentRangeOptions: SelectOption<string>[] = [
  { value: ALL, label: 'All Dates' },
  { value: 'today', label: 'Today' },
  { value: 'this-week', label: 'This Week' },
  { value: 'this-month', label: 'This Month' },
  { value: 'last-30', label: 'Last 30 Days' },
  ...dateRanges.map((range) => ({
    value: range.id,
    label: range.label,
  })),
]

const withinRange = (paidAt: string, rangeId: string) => {
  if (!rangeId || rangeId === ALL) return true

  const day = paidAt.slice(0, 10)

  if (rangeId === 'today') {
    const today = new Date().toISOString().slice(0, 10)
    return day === today
  }

  if (rangeId === 'this-week') {
    const now = new Date()
    const startOfWeek = new Date(now)
    startOfWeek.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1))
    const endOfWeek = new Date(startOfWeek)
    endOfWeek.setDate(startOfWeek.getDate() + 6)
    const from = startOfWeek.toISOString().slice(0, 10)
    const to = endOfWeek.toISOString().slice(0, 10)
    return day >= from && day <= to
  }

  if (rangeId === 'this-month') {
    const now = new Date()
    const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)
    return day >= from && day <= to
  }

  if (rangeId === 'last-30') {
    const now = new Date()
    const from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    const to = now.toISOString().slice(0, 10)
    return day >= from && day <= to
  }

  if (rangeId.startsWith('custom:')) {
    const [from, to] = rangeId.replace('custom:', '').split('_')
    if (from && to) return day >= from && day <= to
    if (from) return day >= from
  }

  const range = dateRanges.find((candidate) => candidate.id === rangeId)
  if (!range) return true

  return day >= range.from && day <= range.to
}

export const filterPayments = (records: PaymentRecord[], filters: PaymentsFilters) => {
  const query = filters.query.trim().toLowerCase()

  return records.filter((payment) => {
    if (query && !payment.driver.toLowerCase().includes(query)) return false
    if (filters.status !== ALL && payment.status !== filters.status) return false
    return withinRange(payment.paidAt, filters.rangeId)
  })
}
